import jQuery from 'jquery';

const ElementType = { TEXT_NODE: 3 };

/**
 * Determines if the element is a script node that will be run after Patcher
 * patches the new source in.
 * @param {Element} element
 * @returns {boolean} True if the element is a runnable script node, false otherwise.
 */
function isRunnableScript(element) {
  if (element.nodeName.toLowerCase() !== 'script') {
    // Not a script
    return false;
  }

  const type = element.getAttribute('type');

  // text/template and module scripts aren't removed from the new source
  // every other script does get removed so we don't consider them for siblings
  //
  // Check reactive_rails.js just before Patcher is instantiated and run
  // to see which script types get removed from the new source.
  return type !== 'text/template' && type !== 'module';
}

export default class Patcher {
  // Prepare to patch the target DOM tree so that it looks like source. The
  // patching is done so that node instances are preserved as much as
  // possible - this will minimize changes to the DOM that is actually in
  // the page.
  constructor(target, source) {
    this.target = target;
    this.source = source;
    this.stats = {
      patched: 0,
      reordered: 0,
      removed: 0,
      inserted: 0,
      preserved: 0,
      timeTaken: 0,
    };
  }

  // Perform the patching by recursively descending (depth-first) into
  // the target and the source in parallel, copying attributes from source
  // to target as possible. When differences are found then the nodes in
  // target are deleted and replaced with the sub-tree from source.
  //
  // This algorithm does not handle depth-changes - e.g. where a new parent
  // is inserted in the middle of a hierarchy. in this case all children of
  // the new parent will be replaced in the target.
  patch() {
    if (!this.target || !this.source) {
      return;
    }

    const startTime = new Date().getTime();
    this.patchDepth(this.target, this.source);
    this.stats.timeTaken = new Date().getTime() - startTime;
  }

  patchDepth(target, source) {
    // Skip elements if the target is marked 'data-reactive-preserve'.
    if (
      target.nodeType !== ElementType.TEXT_NODE &&
      target.attributes &&
      target.getAttribute('data-reactive-preserve')
    ) {
      this.stats.preserved++;
      return;
    }

    // Copy the target's data-react-class attribute to determine the current mounted state of the element
    const previousReactClass =
      target.nodeType !== ElementType.TEXT_NODE &&
      target.attributes &&
      target.getAttribute('data-react-class');
    const previousReactProps =
      previousReactClass && target.getAttribute('data-react-props');

    // Copy attributes from source to target the target wants its attributes
    // preserved.
    if (
      target.nodeType === ElementType.TEXT_NODE ||
      !target.attributes ||
      !target.getAttribute('data-reactive-preserve-attributes')
    ) {
      this.copyAttributes(target, source);
    }

    if (
      source.nodeType !== ElementType.TEXT_NODE &&
      source.attributes &&
      source.getAttribute('data-supports-reactive-update') === 'true'
    ) {
      // If the props haven't changed, there's no need to re-render/mount the component
      if (
        previousReactClass &&
        previousReactClass === source.getAttribute('data-react-class') &&
        previousReactProps === source.getAttribute('data-react-props')
      ) {
        this.stats.preserved++;
        return;
      }

      target.setAttribute(
        'data-react-props',
        source.getAttribute('data-react-props')
      );

      // If the target didn't have a data-react-class attribute, then it has not mounted yet. We should assume the
      // source innerHTML is correct, and the target's innerHTML might be incorrect.
      if (!previousReactClass) {
        // this is rendered HTML, scripting seems valid.
        // eslint-disable-next-line no-unsanitized/property
        target.innerHTML = source.innerHTML;
      } else if (target.dataset.reactClass) {
        window.mountNode(target);
      }

      this.stats.preserved++;
      return;
    } else if (
      target &&
      target.nodeType !== ElementType.TEXT_NODE &&
      target.attributes &&
      target.getAttribute('data-supports-reactive-update') === 'true' &&
      source &&
      source.attributes &&
      source.getAttribute('class') &&
      source.getAttribute('class').indexOf('react-in-place-wrapper') >= 0
    ) {
      target.parentNode.insertBefore(source, target);
      // Prevent memory leaks on cached data-attributes
      jQuery.cleanData([target, ...target.getElementsByTagName('*')]);

      target.remove();
      this.stats.patched++;
      return;
    }

    // Special case for empty source.
    if (!source.hasChildNodes() && target.hasChildNodes()) {
      // Prevent memory leaks on cached data-attributes
      jQuery.cleanData(target.getElementsByTagName('*'));

      while (target.firstChild) target.removeChild(target.firstChild);
      this.stats.removed++; // This only counts 1 but potentially many nodes were removed.
      return;
    }

    // Iterate over each child of the target and source. The loop is
    // controlled by the source, we manipulate the target as we go to make
    // it look like source.
    let sourceChild = source.firstChild;
    let targetChild = target.firstChild;

    while (sourceChild) {
      const sourceSibling = sourceChild.nextSibling; // Read siblings before we change the target.
      var targetSibling;
      if (targetChild) {
        targetSibling = targetChild.nextSibling;
      }
      let advanceSource = true;
      let advanceTarget = true;

      if (targetChild) {
        if (
          sourceChild.nodeName === targetChild.nodeName &&
          sourceChild.id &&
          sourceChild.id.length > 0 &&
          sourceChild.id === targetChild.id
        ) {
          // Source and target are the same node with the same ID.
          this.patchDepth(targetChild, sourceChild);
          this.stats.patched++;
        } else if (
          sourceChild.nodeName === targetChild.nodeName &&
          (!sourceChild.id || sourceChild.id.length === 0) &&
          (!targetChild.id || targetChild.id.length === 0)
        ) {
          // Source and target don't have IDs, but are the same type of node and
          // have the same class so assume they match and recursively copy.
          this.patchDepth(targetChild, sourceChild);
          this.stats.patched++;
        } else if (isRunnableScript(targetChild)) {
          // The target is a runnable script that would not be present in the source
          // Move on to the next targetChild while keeping the same sourceChild
          // essentially just skipping over the script node

          advanceSource = false; // Don't advance the source child
          this.stats.preserved++;
        } else {
          // Something has changed, so try to match the old and new nodes as
          // best we can.

          const insertChild = function (newChild) {
            advanceTarget = false; // Don't advance the target child.
            return target.insertBefore(newChild, targetChild);
          };

          if (sourceChild.id && sourceChild.id.length > 0) {
            // See if the same element appears in a different position in the list.
            const targetMatch = target.children.namedItem(sourceChild.id);
            if (targetMatch) {
              // The list has been reordered, reorder the target.
              advanceTarget = false;
              const newTargetChild = targetMatch;
              target.removeChild(newTargetChild);
              insertChild(newTargetChild);
              this.patchDepth(newTargetChild, sourceChild);
              this.stats.reordered++;
            } else {
              // We can't find a match between the source and target, just
              // insert the source node on the assumption that it is new.
              insertChild(sourceChild);
              this.stats.inserted++;
            }
          } else {
            // We can't find a match between the source and target, just
            // insert the source node on the assumption that it is new.
            insertChild(sourceChild);
            this.stats.inserted++;
          }
        }
      } else {
        // Target is empty or finished, just copy over the source nodes.
        target.appendChild(sourceChild);
        this.stats.inserted++;
      }

      // Iterate.
      if (advanceSource) {
        sourceChild = sourceSibling;
      }
      if (targetChild && advanceTarget) {
        targetChild = targetSibling;
      }
    }

    // Remove remaining target nodes that were not in the source.
    while (targetChild) {
      const oldChild = targetChild;

      if (targetChild) {
        targetChild = targetChild.nextSibling;
      }

      if (oldChild.dataset && oldChild.dataset.reactivePreserve) {
        this.stats.preserved++;
      } else {
        oldChild.remove();
        this.stats.removed++;
      }
    }
  }

  // Copy attributes from one node to another. Note that this does
  // *NOT* remove attributes from the target that are not in the
  // source. This is by design: We often add attributes like `class`
  // and `style` to change how elements are displayed, and we don't
  // want those removed. You can override an attribute by setting its
  // value explicitly in the source. If you really want to remove attributes
  // that are not in the source, you can do so by naming the attributes in
  // reactiveRemoveMissingAttrs. You can also preserve attributes on an
  // individual basis with reactivePreserveAttributesOnly.
  copyAttributes(target, source) {
    if (source.nodeType === ElementType.TEXT_NODE) {
      // For text nodes just copy the value
      target.textContent = source.textContent;
    } else if (source.attributes) {
      // Comments do not have attributes
      const preserveAttributes = target.getAttribute(
        'data-reactive-preserve-attributes-only'
      );
      const exceptions = preserveAttributes
        ? preserveAttributes.split(',')
        : [];

      for (let attr of Array.from(source.attributes)) {
        if (exceptions.indexOf(attr.name) > -1) {
          continue;
        }

        if (target.getAttribute(attr.name) !== source.getAttribute(attr.name)) {
          // Only set if the value is different, otherwise we trigger actions
          // like reloading iframes.
          target.setAttribute(attr.name, attr.value);

          if (attr.name.includes('data-')) {
            const dataName = attr.name.slice(5);
            // prevent stale reads from jQuery's .data method
            jQuery(target).removeData(dataName);
          }
        }
      }

      if (source.getAttribute('data-reactive-dynamic-checkbox')) {
        target.checked = !!source.attributes.checked;
      }

      // Remove the given attributes from the target if they are present on the
      // target but missing on the source
      const removeMissingAttrs = target.getAttribute(
        'data-reactive-remove-missing-attrs'
      );
      if (removeMissingAttrs) {
        removeMissingAttrs.split(',').forEach(attr => {
          if (target.hasAttribute(attr) && !source.hasAttribute(attr)) {
            target.removeAttribute(attr);
            if (attr.includes('data-')) {
              const dataName = attr.slice(5);
              // prevent stale reads from jQuery's .data method
              jQuery(target).removeData(dataName);
            }
          }
        });
      }
    }
  }

  dumpStats() {
    return (
      `Patcher ran in ${this.stats.timeTaken}ms,` +
      ` patched=${this.stats.patched}` +
      ` reordered=${this.stats.reordered}` +
      ` removed=${this.stats.removed}` +
      ` inserted=${this.stats.inserted}` +
      ` preserved=${this.stats.preserved}`
    );
  }
}
